/**
 * Leaderboard — points ranking with the signed-in member's own rank pinned
 * below the list (mobile drawer/leaderboard parity).
 */
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useLeaderboard from '@wlb/shared/hooks/useLeaderboard';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import type { LeaderBoardItem } from '@wlb/shared/types/userProfile';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../features/more/more.css';

export function Leaderboard() {
  const { t } = useTranslation();
  const openUserProfile = useOpenUserProfile();
  const board = useLeaderboard();

  const displayName = (item: LeaderBoardItem): string =>
    getUsernameDisplay(item, t('leaderboard.defaultUserName'));

  const renderBody = () => {
    if (board.loading && board.items.length === 0) return <MoreLoading />;

    if (board.error && board.items.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={board.error || t('leaderboard.loadError')}
          onAction={board.refresh}
          busy={board.loading}
        />
      );
    }

    if (board.loaded && board.items.length === 0) {
      return (
        <ListStatus
          variant="empty"
          title={t('leaderboard.empty.title')}
          message={t('leaderboard.empty.description')}
          onAction={board.refresh}
          busy={board.loading}
        />
      );
    }

    return (
      <>
        <ul className="more-list" aria-label={t('leaderboard.accessibility.list')}>
          {board.items.map((item, index) => {
            const name = displayName(item);
            return (
              <li
                key={item.userId}
                className={`leaderboard-row${index === 0 ? ' leaderboard-row--top' : ''}`}
              >
                <span className="leaderboard-row__rank">
                  {t('leaderboard.rank', { rank: item.rank || index + 1 })}
                </span>
                <button
                  type="button"
                  className="leaderboard-row__main"
                  aria-label={t('leaderboard.accessibility.openProfile', { name })}
                  onClick={() => openUserProfile(item.userGuid)}
                >
                  <Avatar
                    src={resolveProfilePictureUrl(item.profilePicture)}
                    name={name}
                    seed={item.userGuid}
                    size="lg"
                  />
                  <span className="leaderboard-row__name">{name}</span>
                </button>
                <span className="leaderboard-row__points">
                  <span className="leaderboard-row__points-value">
                    {(item.totalPoints ?? 0).toLocaleString()}
                  </span>
                  <span className="leaderboard-row__points-label">
                    {t('leaderboard.pointsLabel')}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>

        {board.hasMore && (
          <div className="more-more">
            <Button
              size="sm"
              variant="outline"
              onClick={board.loadMore}
              disabled={board.loadingMore}
            >
              {t(board.loadingMore ? 'connections.loadingMore' : 'connections.loadMore')}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <MorePage title={t('leaderboard.title')}>
      {renderBody()}

      {board.currentUser && (
        <section className="leaderboard-self">
          <div className="leaderboard-self__row">
            <span className="leaderboard-self__label">{t('leaderboard.yourRank')}</span>
            <span className="leaderboard-self__rank">
              {t('leaderboard.rank', { rank: board.currentUser.rank })}
            </span>
            <span className="leaderboard-row__points">
              <span className="leaderboard-row__points-value">
                {(board.currentUser.totalPoints ?? 0).toLocaleString()}
              </span>
              <span className="leaderboard-row__points-label">
                {t('leaderboard.pointsLabel')}
              </span>
            </span>
          </div>
          <p className="leaderboard-self__motivation">{t('leaderboard.motivation')}</p>
        </section>
      )}
    </MorePage>
  );
}
